import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { CheckCircle, XCircle, Clock, DoorOpen, Door as DoorClosed, QrCode, Money, Calendar } from "@phosphor-icons/react";

const STATUS_MAP = {
  awaiting_hotel: { label: "Awaiting Confirm", color: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle },
  checked_in: { label: "Checked In", color: "bg-indigo-50 text-indigo-800 border-indigo-200", icon: DoorOpen },
  checked_out: { label: "Checked Out", color: "bg-neutral-100 text-neutral-700 border-neutral-200", icon: DoorClosed },
  cancelled: { label: "Cancelled", color: "bg-red-50 text-red-800 border-red-200", icon: XCircle },
  pending_payment: { label: "Pending Payment", color: "bg-neutral-100 text-neutral-600 border-neutral-200", icon: Clock },
};

export default function PartnerBookingsPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(null); // { booking_id, mode: 'in'|'out' }

  const load = () => {
    setLoading(true);
    api.get("/partner/bookings").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const counts = rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

  const doAccept = async (id) => {
    try {
      await api.post(`/partner/bookings/${id}/accept`);
      toast.success("Booking confirmed ✓");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const doReject = async (id) => {
    const reason = window.prompt("Reason for rejecting (optional):", "");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/partner/bookings/${id}/reject`, { reason });
      toast.success(`Rejected. Refund ₹${data.refund_amount || 0} will be issued.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const doCheckin = async (r, mode) => {
    // Ask guest to show QR — use manual token entry (mobile keyboards) OR fetch guest QR from admin
    const token = window.prompt(`Enter check-${mode} token from guest's QR code:`, "");
    if (!token) return;
    try {
      const url = mode === "in" ? "/partner/checkin" : "/partner/checkout";
      const { data } = await api.post(url, { booking_id: r.id, token: token.trim() });
      toast.success(`Check-${mode} done · ${data.guest_name || r.user_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid QR / not eligible"); }
  };

  const FilterChip = ({ value, label }) => {
    const active = filter === value;
    const c = value === "all" ? rows.length : counts[value] || 0;
    return (
      <button onClick={() => setFilter(value)} data-testid={`filter-${value}`}
        className={`px-4 py-2 rounded-full text-xs tracking-wide border ${
          active ? "bg-midnight text-bone border-midnight" : "bg-white text-neutral-600 border-black/10"
        }`}>
        {label} <span className="ml-1 text-[10px] opacity-70">({c})</span>
      </button>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="partner-bookings-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Hotel Partner</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-8">Manage Bookings</h1>

      <div className="flex gap-2 mb-6 flex-wrap">
        <FilterChip value="all" label="All" />
        <FilterChip value="confirmed" label="Confirmed" />
        <FilterChip value="checked_in" label="Checked In" />
        <FilterChip value="checked_out" label="Completed" />
        <FilterChip value="cancelled" label="Cancelled" />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-stone rounded-xl animate-pulse"/>)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-black/5" data-testid="no-bookings">
          <p className="font-serif text-xl text-midnight">No bookings in this view</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const s = STATUS_MAP[r.status] || STATUS_MAP.pending_payment;
            const StatusIcon = s.icon;
            return (
              <div key={r.id} className="bg-white border border-black/5 rounded-2xl p-5 flex flex-col md:flex-row gap-4" data-testid={`row-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-serif text-lg text-midnight">{r.user_name}</p>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${s.color} inline-flex items-center gap-1`}>
                      <StatusIcon size={11} weight="fill" /> {s.label}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 mb-2">{r.hotel_name} · {r.hotel_city}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><Calendar size={10} className="inline text-gold mr-1" />{new Date(r.check_in).toLocaleDateString("en-IN")} → {new Date(r.check_out).toLocaleDateString("en-IN")}</div>
                    <div>👥 {r.guests} guest(s)</div>
                    <div>🚪 {r.rooms} room(s)</div>
                    <div className="font-semibold text-midnight"><Money size={10} className="inline text-gold mr-1" />₹{(r.total_amount || 0).toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-start md:items-end md:flex-col md:justify-center">
                  {(r.status === "confirmed" || r.status === "awaiting_hotel") && r.payment_status === "paid" && (
                    <>
                      {r.status === "awaiting_hotel" && (
                        <button onClick={() => doAccept(r.id)} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid={`accept-${r.id}`}>Accept</button>
                      )}
                      <button onClick={() => doCheckin(r, "in")} className="px-4 py-2 rounded-full border border-midnight text-midnight text-xs font-semibold inline-flex items-center gap-1" data-testid={`checkin-${r.id}`}>
                        <QrCode size={12} weight="bold" /> Check-in
                      </button>
                      <button onClick={() => doReject(r.id)} className="px-4 py-2 rounded-full border border-red-300 text-red-600 text-xs font-semibold" data-testid={`reject-${r.id}`}>Reject</button>
                    </>
                  )}
                  {r.status === "checked_in" && (
                    <button onClick={() => doCheckin(r, "out")} className="px-4 py-2 rounded-full bg-midnight text-bone text-xs font-semibold inline-flex items-center gap-1" data-testid={`checkout-${r.id}`}>
                      <QrCode size={12} weight="bold" /> Check-out
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
